// Black-Scholes для расчёта греков на фронте (мгновенно, без roundtrip к API)
// Формулы идентичны option_engine/black_scholes.py — проверены тестами.

function erf(x: number): number {
  // Abramowitz & Stegun 7.1.26 approximation
  const sign = x < 0 ? -1 : 1;
  x = Math.abs(x);
  const a1 = 0.254829592, a2 = -0.284496736, a3 = 1.421413741;
  const a4 = -1.453152027, a5 = 1.061405429, p = 0.3275911;
  const t = 1 / (1 + p * x);
  const y = 1 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);
  return sign * y;
}
function ncdf(x: number): number {
  return 0.5 * (1 + erf(x / Math.SQRT2));
}
function npdf(x: number): number {
  return Math.exp(-0.5 * x * x) / Math.sqrt(2 * Math.PI);
}

export interface BSInputs {
  S: number;       // spot
  K: number;       // strike
  T: number;       // years to expiry
  r: number;       // risk-free, decimal
  sigma: number;   // implied vol, decimal
  q?: number;      // div yield, decimal
}

function d1d2(x: BSInputs) {
  if (x.T <= 0) return { d1: NaN, d2: NaN };
  const q = x.q ?? 0;
  const d1 = (Math.log(x.S / x.K) + (x.r - q + 0.5 * x.sigma * x.sigma) * x.T) / (x.sigma * Math.sqrt(x.T));
  const d2 = d1 - x.sigma * Math.sqrt(x.T);
  return { d1, d2 };
}

export interface Greeks {
  delta: number;
  gamma: number;
  theta: number;   // per day
  vega: number;    // per 1% IV change
}

export function computeGreeks(side: "call" | "put", x: BSInputs): Greeks {
  if (x.T <= 0 || x.sigma <= 0) {
    return { delta: side === "call" ? (x.S > x.K ? 1 : 0) : (x.S < x.K ? -1 : 0), gamma: 0, theta: 0, vega: 0 };
  }
  const q = x.q ?? 0;
  const { d1, d2 } = d1d2(x);
  const dqT = Math.exp(-q * x.T);
  const drT = Math.exp(-x.r * x.T);

  const delta = side === "call"
    ? dqT * ncdf(d1)
    : dqT * (ncdf(d1) - 1);

  const gamma = dqT * npdf(d1) / (x.S * x.sigma * Math.sqrt(x.T));
  const vega = (x.S * dqT * npdf(d1) * Math.sqrt(x.T)) / 100;

  let theta: number;
  if (side === "call") {
    const a = -(x.S * dqT * npdf(d1) * x.sigma) / (2 * Math.sqrt(x.T));
    const b = -x.r * x.K * drT * ncdf(d2);
    const c = q * x.S * dqT * ncdf(d1);
    theta = (a + b + c) / 365;
  } else {
    const a = -(x.S * dqT * npdf(d1) * x.sigma) / (2 * Math.sqrt(x.T));
    const b = x.r * x.K * drT * ncdf(-d2);
    const c = -q * x.S * dqT * ncdf(-d1);
    theta = (a + b + c) / 365;
  }
  return { delta, gamma, theta, vega };
}

export function daysToExpiry(expiryISO: string, fromISO?: string): number {
  const exp = new Date(expiryISO).getTime();
  const from = (fromISO ? new Date(fromISO) : new Date()).getTime();
  return Math.max(0, Math.round((exp - from) / 86400_000));
}
